import React, { useMemo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import PaginationControl from "./table-component/PaginationControl";

const renderBoldHeader = (params) => (
  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
    {params?.colDef?.headerName || ""}
  </Typography>
);

const CustomDataGridPro = ({
  rows,
  columns,
  isLoading,
  isSkeletonOnly = false,
  page,
  setPage,
  pageSize,
  setPageSize,
  getRowClassName,
  totalCount,
  extraFooter,
}) => {
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = rows.slice(
    (currentPage - 1) * pageSize,
    pageSize * currentPage
  );

  // Memoized version of columns with bold headers
  const enhancedColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      renderHeader: col.renderHeader || renderBoldHeader,
    }));
  }, [columns]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ height: 540, width: "100%" }}>
        {isSkeletonOnly || isLoading ? (
          <Box
            sx={{
              height: 540,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#fafafa",
              boxShadow: 2,
            }}
          >
            {/* Fake Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f4f6f8",
                borderBottom: "1px solid #ddd",
                px: 2,
                py: 1,
              }}
            >
              {enhancedColumns.map((col, i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  width={col.width || 100}
                  height={28}
                  sx={{ flexShrink: 0, mr: 2 }}
                />
              ))}
            </Box>

            {/* Fake Rows */}
            {Array.from({ length: pageSize }).map((_, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f7fbff",
                }}
              >
                {enhancedColumns.map((col, colIndex) => (
                  <Skeleton
                    key={colIndex}
                    variant="rectangular"
                    width={col.width || 100}
                    height={24}
                    sx={{ flexShrink: 0, mr: 2, borderRadius: 1 }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <DataGridPro
            rows={paginatedRows}
            columns={enhancedColumns}
            disableColumnMenu
            disableSelectionOnClick
            pagination={false}
            hideFooter
            getRowClassName={getRowClassName}
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f4f6f8",
                fontWeight: "bold",
                fontSize: "0.9rem",
                borderBottom: "1px solid #ddd",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "0.875rem",
                color: "#333",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#f7fbff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#e3f2fd",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .unread-alert-row": {
                borderLeft: "4px solid #1976d2",
                backgroundColor: "#ffffff !important",
                fontWeight: 600,
              },
              "& .resolved-alert-row": {
                backgroundColor: "#e6f4ea !important",
              },
            }}
          />
        )}
      </Box>

      <PaginationControl
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      {extraFooter && <Box mt={2}>{extraFooter}</Box>}
    </Box>
  );
};

CustomDataGridPro.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  isSkeletonOnly: PropTypes.bool,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  getRowClassName: PropTypes.func,
  totalCount: PropTypes.number.isRequired,
  extraFooter: PropTypes.node,
};

export default CustomDataGridPro;